import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { usePageMeta } from '../hooks/usePageMeta';
import SafeIcon from '../common/SafeIcon';
import ScrollToTop from '../components/ScrollToTop';
import Reveal from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import * as FiIcons from 'react-icons/fi';
import videoThumb from '../assets/images/HTMX-und-PHP.webp';

const { FiSmartphone, FiServer, FiShield, FiGitBranch, FiArrowRight, FiMail } = FiIcons;

const Home = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  usePageMeta({ title: t.hero.title, description: t.hero.description, path: '' });

  const features = [
    { icon: FiShield, title: t.features.nocms.title, description: t.features.nocms.description },
    { icon: FiSmartphone, title: t.features.responsive.title, description: t.features.responsive.description },
    { icon: FiServer, title: t.features.hosting.title, description: t.features.hosting.description },
    { icon: FiGitBranch, title: t.features.ownership.title, description: t.features.ownership.description },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <section className="py-20 relative min-h-[520px] flex items-center hero-gradient-home">
        <div className="absolute inset-0 bg-brand-dark/50"></div>
        {/* Ambient-Bewegung: driftendes Leuchten */}
        <div aria-hidden="true" data-hero-glow className="absolute inset-0 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 82% 20%, rgba(39,80,140,0.55), transparent 46%)', animation: 'lzDrift 17s ease-in-out infinite alternate' }}></div>
        <div aria-hidden="true" data-hero-glow className="absolute inset-0 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 18% 82%, rgba(196,168,98,0.16), transparent 42%)', animation: 'lzGlow 13s ease-in-out infinite alternate' }}></div>
        {/* Ambient-Bewegung: aufsteigende Bernstein-Partikel */}
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { l: '12%', s: 8, d: 9, delay: -1 },
            { l: '24%', s: 5, d: 11, delay: -4 },
            { l: '38%', s: 10, d: 8, delay: -2.5 },
            { l: '51%', s: 6, d: 12, delay: -6 },
            { l: '63%', s: 7, d: 10, delay: -0.5 },
            { l: '74%', s: 5, d: 13, delay: -7.5 },
            { l: '86%', s: 9, d: 9.5, delay: -3.5 },
            { l: '44%', s: 4, d: 14, delay: -9 },
            { l: '69%', s: 5, d: 11.5, delay: -5 },
          ].map((p, i) => (
            <span
              key={i}
              data-hero-particle
              style={{
                position: 'absolute', bottom: '-16px', left: p.l,
                width: p.s, height: p.s, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(252,211,77,.9), rgba(252,211,77,0) 70%)',
                animation: `lzFloat ${p.d}s linear infinite`, animationDelay: `${p.delay}s`,
              }}
            ></span>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-xs font-bold uppercase tracking-eyebrow text-amber-300 mb-5"
            >
              Webdesign für kleine Unternehmen
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-4xl md:text-6xl font-bold text-white mb-6"
            >
              {t.hero.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif italic text-xl md:text-2xl text-amber-100 mb-8"
            >
              {t.hero.subtitle}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="text-lg text-stone-300 mb-10 max-w-2xl mx-auto leading-relaxed"
            >
              {t.hero.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/request" className="inline-flex items-center justify-center px-8 py-4 bg-amber-700 text-white font-semibold rounded-sm hover:bg-amber-800 transition-colors duration-200 shadow-md">
                {t.hero.cta}
                <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-sm border border-white/55 hover:bg-white/10 transition-colors duration-200">
                {t.nav.services}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-16">
            <SectionHeading eyebrow="Warum Alitero" title={t.features.title} />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Reveal
                key={index}
                delay={index * 0.1}
                whileHover={{ y: -6 }}
                className="text-center p-8 rounded-sm bg-white border border-stone-200 shadow-sm hover:shadow-lg transition-shadow duration-200"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotate: -4 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 text-amber-800 rounded-full mb-4"
                >
                  <SafeIcon icon={feature.icon} className="h-8 w-8" />
                </motion.div>
                <h3 className="font-serif text-xl font-bold text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-500 leading-relaxed">{feature.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison — dunkles Navy-Panel */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="relative overflow-hidden rounded-sm p-8 md:p-12 shadow-2xl bg-gradient-to-br from-navy-900 to-brand-dark">
            <SectionHeading eyebrow="Zwei technische Ansätze" title={t.comparison.title} onBrand />

            {/* Ein Ansatz je Spalte — die Anzahl der Sections bestimmt das Grid. */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
              {t.comparison.sections.map((section, si) => (
                <div key={si} className="space-y-4">
                  <h3 className="font-serif text-2xl font-bold text-white">{section.heading}</h3>
                  {section.description && section.description.map((p, pi) => (
                    <p key={pi} className="text-stone-300 leading-relaxed">{p}</p>
                  ))}
                  {section.groups && section.groups.map((group, gi) => (
                    <div key={gi} className="mt-4">
                      {group.title && <p className="font-semibold text-amber-300">{group.title}</p>}
                      <ul className="mt-1 space-y-1">
                        {group.items.map((item, ii) => (
                          <li key={ii} className="flex gap-2 text-stone-300 leading-relaxed">
                            <span className="text-amber-400 font-bold">–</span><span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-white/25">
              <p className="font-serif text-xl font-bold text-white mb-4">{t.comparison.recommendation.title}</p>
              <ul className="space-y-2">
                {t.comparison.recommendation.items.map((item, i) => (
                  <li key={i} className="flex gap-2 text-stone-300 leading-relaxed">
                    <span className="text-amber-400 font-bold">–</span>
                    <span>
                      {item.label && <strong className="text-white">{item.label} </strong>}
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Video */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex justify-center">
            <a href="https://youtu.be/UuE0reByaHk" target="_blank" rel="noopener noreferrer"
               className="block rounded-sm overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img src={videoThumb} alt="HTMX und PHP – eine unschlagbare Kombination?" className="w-full h-auto max-w-2xl" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Target */}
      <section className="py-24 bg-stone-100 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Reveal className="mb-14">
              <SectionHeading eyebrow="Für wen ich arbeite" title={t.target.title} subtitle={t.target.description} />
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.target.items.map((item, index) => (
                <Reveal
                  key={index}
                  delay={(index % 3) * 0.1}
                  whileHover={{ y: -4 }}
                  className="bg-white p-6 rounded-sm shadow-sm border border-stone-200 hover:shadow-lg hover:border-amber-300 transition-all duration-200 cursor-pointer"
                >
                  <p className="text-stone-800 font-semibold">{item}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — Navy-Band */}
      <section className="pt-24 pb-32 relative overflow-hidden hero-gradient-services">
        <div className="absolute inset-0 bg-brand-dark/50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <SectionHeading eyebrow="Kontakt" title={t.contact.title} subtitle={t.contact.description} onBrand />
          </Reveal>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request" className="inline-flex items-center justify-center px-8 py-4 bg-amber-700 text-white font-semibold rounded-sm hover:bg-amber-800 transition-colors duration-200 shadow-md">
              <SafeIcon icon={FiMail} className="mr-2 h-5 w-5" />
              {t.hero.cta}
            </Link>
            <Link to="/services" className="inline-flex items-center justify-center px-8 py-4 border border-white/55 text-white font-semibold rounded-sm hover:bg-white/10 transition-colors duration-200">
              {t.nav.services}
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <ScrollToTop />
    </div>
  );
};

export default Home;
